/**
 * The mobile replay contract: what the NativeScript recorder writes and what the
 * web player renders.
 *
 * It lives in the platform-neutral lib because it has two sides on two platforms
 * that cannot import each other — the recorder is `platform:nativescript`, the
 * player is `platform:web`. A second copy on either side is how the two would
 * drift, and drift here is silent: ingest treats `payload` as opaque, so a tree
 * the player no longer understands still stores fine and answers 202.
 */
import { RYN_REPLAY_MASKED_SSN } from './ryn-replay-masking-policy';

/**
 * A complete serialized view tree, emitted on interaction when the tree changed.
 *
 * Every frame is a WHOLE tree, not a delta against the previous one. That is
 * what lets a player show any moment by drawing the latest frame at or before
 * it, and what makes a session whose opening frame was lost still playable from
 * the next one.
 */
export const RYN_REPLAY_MOBILE_FRAME_EVENT_TYPE = 'MobileFrame';

/**
 * A touch, with the node it landed on in the frame current at the time.
 *
 * `kind` is `down` / `move` / `up` while a finger is on the glass, and
 * `tap` / `longpress` / `pan` on release — so the release event alone carries
 * the gesture, and an `up` only appears when its `down` was never seen.
 */
export const RYN_REPLAY_MOBILE_TOUCH_EVENT_TYPE = 'MobileTouch';

/**
 * Version of the frame payload schema, carried INSIDE the payload because ingest
 * never validates it. A player reading a version it does not know can say so;
 * a player reading an unversioned tree can only guess.
 */
export const RYN_REPLAY_MOBILE_FRAME_SCHEMA_VERSION = 2;

/**
 * Synthetic node the recorder places under an open modal: a full-screen dim
 * layer, so the page behind a dialog reads as behind it. It has no view on the
 * device.
 */
export const RYN_REPLAY_MOBILE_SCRIM_NODE_TYPE = 'ModalScrim';

/**
 * Synthetic node for a tab bar the platform draws natively (the iOS
 * `UITabBar` behind a NativeScript `TabView`), which has no NativeScript view
 * of its own to serialize. Its children are `TabViewItem` nodes.
 */
export const RYN_REPLAY_MOBILE_TAB_BAR_NODE_TYPE = 'NativeTabBar';

/**
 * Presentation of one view. Every field is optional and absent means "the
 * platform default", so an unstyled view costs nothing on the wire.
 *
 * Colors are CSS `rgba()` strings. Lengths are DIPs. Nothing here is content:
 * it is what a screen LOOKS like, never what it says.
 */
export interface IRynReplayViewStyle {
  background?: string;
  color?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  opacity?: number;
  fontSize?: number;
  fontWeight?: string;
  /**
   * Only ever an icon font. A text font is not recorded: the player cannot load
   * the app's bundled fonts, and an icon family is what decides whether a glyph
   * renders as an icon or as a tofu box.
   */
  iconFont?: string;
  textAlign?: 'left' | 'center' | 'right';
}

/** One view. `frame` is window-absolute, in device-independent pixels. */
export interface IRynReplayViewNode {
  type: string;
  frame: { x: number; y: number; w: number; h: number };
  className?: string;
  id?: string;
  text?: string;
  /** True when this view's visual region must be masked in playback. */
  masked?: boolean;
  style?: IRynReplayViewStyle;
  children?: IRynReplayViewNode[];
}

/** True when a serialized view node carries a masked value or explicit field mask. */
export const isRynReplayNodeMasked = (
  node: Pick<IRynReplayViewNode, 'masked' | 'text'> | undefined
): boolean =>
  !!node &&
  (node.masked === true ||
    (typeof node.text === 'string' &&
      (node.text.includes(RYN_REPLAY_MASKED_SSN) ||
        /^[•\n]+$/.test(node.text))));

/** True when any node in the tree carries a masked SSN or explicit mask. */
export const rynReplayTreeHasMaskedSsn = (
  root: IRynReplayViewNode | undefined
): boolean => {
  if (!root) return false;
  const pending = [root];
  while (pending.length > 0) {
    const node = pending.pop()!;
    if (isRynReplayNodeMasked(node)) return true;
    if (node.children) pending.push(...node.children);
  }
  return false;
};

/**
 * Payload of a mobile frame. Sent under `MobileFrame`, and under the shared
 * `FullSnapshot` token for keyframes (navigation, dialog, resume) so the
 * platform-neutral eviction rules protect them.
 */
export interface IRynReplayViewTreeFrame {
  v: number;
  /** Optional, already masked on-device. Older recordings remain tree-only. */
  screenshot?: IRynReplayMobileScreenshot;
  /** Privacy-safe diagnostic when an opted-in screenshot capture was skipped. */
  screenshotSkipReason?: string;
  root?: IRynReplayViewNode;
  /** Nodes actually serialized. */
  nodes: number;
  /**
   * True when a depth or node ceiling stopped the walk short, so a frame that
   * hit a ceiling is distinguishable from a screen that really was that shallow.
   */
  truncated: boolean;
}

/** Bounded JPEG, never a remote URL or arbitrary data URI. */
export interface IRynReplayMobileScreenshot {
  v: 1;
  jpegBase64: string;
  width: number;
  height: number;
  viewportWidth: number;
  viewportHeight: number;
  captureMs: number;
  encodeMs: number;
}

export const RYN_REPLAY_MAX_SCREENSHOT_BYTES = 96 * 1024;
export const RYN_REPLAY_MAX_SCREENSHOT_EDGE = 720;

/** Validate at both the recorder and player boundaries. */
export const readRynReplayMobileScreenshot = (
  value: unknown
): IRynReplayMobileScreenshot | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const shot = value as IRynReplayMobileScreenshot;
  if (
    shot.v !== 1 ||
    typeof shot.jpegBase64 !== 'string' ||
    shot.jpegBase64.length >
      4 * Math.ceil(RYN_REPLAY_MAX_SCREENSHOT_BYTES / 3) ||
    !/^\/9j\/[A-Za-z0-9+/]*={0,2}$/.test(shot.jpegBase64) ||
    shot.jpegBase64.length % 4 !== 0 ||
    ![shot.width, shot.height].every(
      (n) => Number.isInteger(n) && n > 0 && n <= RYN_REPLAY_MAX_SCREENSHOT_EDGE
    ) ||
    ![shot.viewportWidth, shot.viewportHeight].every(
      (n) => Number.isFinite(n) && n > 0 && n <= 4096
    ) ||
    ![shot.captureMs, shot.encodeMs].every((n) => Number.isFinite(n) && n >= 0)
  )
    return undefined;
  return shot;
};

/** Payload of a `MobileTouch`. Coordinates are window DIPs, same space as frames. */
export interface IRynReplayMobileTouchPayload {
  kind: string;
  x: number;
  y: number;
  target?: Pick<IRynReplayViewNode, 'type' | 'id' | 'className' | 'frame'>;
  /** On `tap` / `longpress` / `pan`: how long the finger was down. */
  durationMs?: number;
  /** On `tap` / `longpress` / `pan`: where the gesture began. */
  startX?: number;
  startY?: number;
}
